import React, { useEffect, useState } from 'react';
import { getPromotions, createPromotion, updatePromotion, deletePromotion, Promotion, Page } from '../api/admin';
import styles from './CommonPage.module.css';
import { Trash2, Plus, Tag, AlertCircle, ChevronLeft, ChevronRight, X, Edit } from 'lucide-react';

const PromotionPage: React.FC = () => {
  const [data, setData] = useState<Page<Promotion> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [newPromo, setNewPromo] = useState<Partial<Promotion>>({ name: '', durationDays: 30, type: 'RELATIVE', fixedEndDate: '', description: '', limitBytes: 10 * 1024 * 1024 * 1024, userType: 'GENERAL' });
  const [editingPromo, setEditingPromo] = useState<Promotion | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [limitGB, setLimitGB] = useState('10');

  useEffect(() => {
    loadPromotions();
  }, [page]);

  const loadPromotions = async () => {
    setLoading(true);
    try {
      const result = await getPromotions(page, 10);
      setData(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handlePrev = () => {
    if (page > 0) setPage(page - 1);
  };

  const handleNext = () => {
    if (data && page < data.totalPages - 1) setPage(page + 1);
  };

  const handleCreateOrUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingPromo) {
        // 백엔드 LocalDateTime 포맷에 맞춰 일시 보정 (날짜만 올 경우 23:59:59 추가)
        const payload = { ...newPromo };
        if (payload.type === 'FIXED') {
          delete payload.durationDays;
          if (payload.fixedEndDate && payload.fixedEndDate.length === 10) {
            payload.fixedEndDate = `${payload.fixedEndDate}T23:59:59`;
          }
        } else {
          delete payload.fixedEndDate;
        }
        await updatePromotion(editingPromo.id, payload as Promotion);
      } else {
        const payload = { ...newPromo };
        if (payload.type === 'FIXED') {
          delete payload.durationDays;
          if (payload.fixedEndDate && payload.fixedEndDate.length === 10) {
            payload.fixedEndDate = `${payload.fixedEndDate}T23:59:59`;
          }
        } else {
          delete payload.fixedEndDate;
        }
        await createPromotion(payload as Promotion);
      }
      setNewPromo({ name: '', durationDays: 30, type: 'RELATIVE', fixedEndDate: '', description: '', limitBytes: 10 * 1024 * 1024 * 1024, userType: 'GENERAL' });
      setLimitGB('10');
      setIsCreating(false);
      setEditingPromo(null);
      loadPromotions();
    } catch (e: any) {
      console.error('Promotion Save Error:', e);
      const msg = e.response?.data?.message || e.message || '알 수 없는 오류';
      alert(`프로모션 저장 실패: ${msg}`);
    }
  };

  const handleEdit = (promo: Promotion) => {
    setEditingPromo(promo);
    const gbValue = promo.limitBytes ? (promo.limitBytes / (1024 * 1024 * 1024)).toString() : '10';
    setLimitGB(gbValue);
    setNewPromo({
      name: promo.name,
      durationDays: promo.durationDays,
      type: promo.type,
      fixedEndDate: promo.fixedEndDate ? promo.fixedEndDate.substring(0, 10) : '',
      description: promo.description || '',
      limitBytes: promo.limitBytes || 10 * 1024 * 1024 * 1024,
      userType: promo.userType || 'GENERAL'
    });
    setIsCreating(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await deletePromotion(id);
      loadPromotions();
    } catch (e) {
      alert('삭제 실패');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>프로모션 관리</h1>
          <p>사용자에게 제공할 프로모션 코드를 관리합니다.</p>
        </div>
        <button className={styles.primaryBtn} onClick={() => setIsCreating(true)}>
          <Plus size={16} /> 프로모션 추가
        </button>
      </div>

      {isCreating && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className={styles.modalHeader}>
              <h3>{editingPromo ? '프로모션 수정' : '프로모션 추가'}</h3>
              <button type="button" onClick={() => { setIsCreating(false); setEditingPromo(null); }} className={styles.closeBtn}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateOrUpdate}>
              <div className={styles.formGroup}>
                <label>대상 회원 유형</label>
                <div className={styles.radioGroup}>
                  <label className={`${styles.radioLabel} ${newPromo.userType === 'GENERAL' ? styles.radioActive : ''}`}>
                    <input
                      type="radio"
                      name="userType"
                      value="GENERAL"
                      checked={newPromo.userType === 'GENERAL'}
                      onChange={() => setNewPromo({ ...newPromo, userType: 'GENERAL' })}
                    /> 일반 회원
                  </label>
                  <label className={`${styles.radioLabel} ${newPromo.userType === 'BUSINESS' ? styles.radioActive : ''}`}>
                    <input
                      type="radio"
                      name="userType"
                      value="BUSINESS"
                      checked={newPromo.userType === 'BUSINESS'}
                      onChange={() => setNewPromo({ ...newPromo, userType: 'BUSINESS' })}
                    /> 사업자 회원
                  </label>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>프로모션 타입</label>
                <div className={styles.radioGroup}>
                  <label className={`${styles.radioLabel} ${newPromo.type === 'RELATIVE' ? styles.radioActive : ''}`}>
                    <input
                      type="radio"
                      name="type"
                      value="RELATIVE"
                      checked={newPromo.type === 'RELATIVE'}
                      onChange={() => setNewPromo({ ...newPromo, type: 'RELATIVE' })}
                    /> 상대적 기간
                  </label>
                  <label className={`${styles.radioLabel} ${newPromo.type === 'FIXED' ? styles.radioActive : ''}`}>
                    <input
                      type="radio"
                      name="type"
                      value="FIXED"
                      checked={newPromo.type === 'FIXED'}
                      onChange={() => setNewPromo({ ...newPromo, type: 'FIXED' })}
                    /> 절대적 기한
                  </label>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>이름</label>
                <input
                  value={newPromo.name}
                  onChange={e => setNewPromo({ ...newPromo, name: e.target.value })}
                  required
                  placeholder="예: 신규 가입 이벤트"
                />
              </div>
              <div className={styles.formGroup}>
                <label>설명 (선택)</label>
                <input
                  value={newPromo.description || ''}
                  onChange={e => setNewPromo({ ...newPromo, description: e.target.value })}
                  placeholder="프로모션에 대한 설명을 입력하세요"
                />
              </div>
              {newPromo.type === 'RELATIVE' ? (
                <div className={styles.formGroup}>
                  <label>기간 (일)</label>
                  <input
                    type="number"
                    value={newPromo.durationDays}
                    onChange={e => setNewPromo({ ...newPromo, durationDays: Number(e.target.value) })}
                    required
                    min="1"
                  />
                </div>
              ) : (
                <div className={styles.formGroup}>
                  <label>종료 날짜</label>
                  <input
                    type="date"
                    value={newPromo.fixedEndDate}
                    onChange={e => setNewPromo({ ...newPromo, fixedEndDate: e.target.value })}
                    required
                  />
                </div>
              )}
              <div className={styles.formGroup}>
                <label>Relay 월 이용가능량 (GB)</label>
                <input
                  type="number"
                  value={limitGB}
                  onChange={e => {
                    setLimitGB(e.target.value);
                    const gb = parseFloat(e.target.value);
                    if (!isNaN(gb) && gb > 0) {
                      setNewPromo({ ...newPromo, limitBytes: Math.round(gb * 1024 * 1024 * 1024) });
                    }
                  }}
                  required
                  min="0.01"
                  step="0.01"
                  placeholder="예: 0.5"
                />
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '4px' }}>
                  릴레이 서버를 통한 월간 트래픽 제한량 (소수점 입력 가능, 예: 0.05GB = 50MB)
                </div>
              </div>
              <div className={styles.formActions} style={{ marginTop: '24px' }}>
                <button type="button" onClick={() => { setIsCreating(false); setEditingPromo(null); }} className={styles.cancelBtn}>취소</button>
                <button type="submit" className={styles.submitBtn}>{editingPromo ? '수정' : '추가'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th>이름 / 설명</th>
              <th>가입 대상</th>
              <th>프로모션 타입 / 기간</th>
              <th>월 제공 트래픽</th>
              <th style={{ textAlign: 'center' }}>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '24px' }}>로딩 중...</td></tr>
            ) : data?.content.map((promo: Promotion) => (
              <tr key={promo.id}>
                <td data-label="ID">{promo.id}</td>
                <td data-label="이름 / 설명">
                  <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>{promo.name}</div>
                  {promo.description && <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{promo.description}</div>}
                </td>
                <td data-label="가입 대상">
                  <span style={{ fontSize: '0.8rem', padding: '2px 8px', backgroundColor: '#e5e7eb', color: '#374151', borderRadius: '12px', whiteSpace: 'nowrap', fontWeight: 500 }}>
                    {promo.userType === 'BUSINESS' ? '사업자' : '일반'}
                  </span>
                </td>
                <td data-label="프로모션 타입 / 기간">
                  {promo.type === 'FIXED' ? (
                    <div>
                      <span style={{ fontSize: '0.8rem', padding: '2px 8px', backgroundColor: '#e0e7ff', color: '#3730a3', borderRadius: '12px', whiteSpace: 'nowrap', fontWeight: 500 }}>
                        절대적 기한
                      </span>
                      <div style={{ fontSize: '0.8rem', marginTop: '4px', color: '#4b5563' }}>
                        종료: {promo.fixedEndDate ? new Date(promo.fixedEndDate).toLocaleDateString() : '-'}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <span style={{ fontSize: '0.8rem', padding: '2px 8px', backgroundColor: '#dcfce7', color: '#166534', borderRadius: '12px', whiteSpace: 'nowrap', fontWeight: 500 }}>
                        상대적 기간
                      </span>
                      <div style={{ fontSize: '0.8rem', marginTop: '4px', color: '#4b5563' }}>
                        기간: {promo.durationDays}일
                      </div>
                    </div>
                  )}
                </td>
                <td data-label="월 제공 트래픽">
                  <div style={{ fontSize: '0.9rem', color: '#2563eb', fontWeight: 500 }}>
                    {promo.limitBytes ? `${(promo.limitBytes / (1024 * 1024 * 1024)).toFixed(2)} GB` : '0 GB'}
                  </div>
                </td>
                <td data-label="관리" style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                    <button
                      onClick={() => handleEdit(promo)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', display: 'flex' }}
                      title="수정"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(promo.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex' }}
                      title="삭제"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {data?.content.length === 0 && !loading && (
              <tr>
                <td colSpan={6}>
                  <div className={styles.emptyState} style={{ border: 'none' }}>
                    <AlertCircle size={48} color="#ccc" />
                    <p>등록된 프로모션이 없습니다.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination} style={{ marginTop: '24px' }}>
        <button onClick={handlePrev} disabled={page === 0}>
          <ChevronLeft size={16} /> 이전
        </button>
        <span>
          {data?.number !== undefined ? data.number + 1 : 1} / {data?.totalPages || 1} 페이지
        </span>
        <button onClick={handleNext} disabled={!data || page >= data.totalPages - 1}>
          다음 <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default PromotionPage;
